import { useCallback, useMemo, useState } from "react";

export type DashboardWidgetId = "network" | "clock" | "astro" | "weather" | "news";

const storageKey = "lab-bookmark.dashboardOrder.v1";
const defaultOrder: DashboardWidgetId[] = ["network", "clock", "astro", "weather", "news"];

const readOrder = () => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return defaultOrder;
    const parsed = JSON.parse(raw) as DashboardWidgetId[];
    const known = new Set(defaultOrder);
    const cleaned = parsed.filter((id) => known.has(id));
    const missing = defaultOrder.filter((id) => !cleaned.includes(id));
    return [...cleaned, ...missing];
  } catch {
    return defaultOrder;
  }
};

const moveById = <T extends string>(items: T[], activeId: T, overId: T) => {
  const from = items.indexOf(activeId);
  const to = items.indexOf(overId);
  if (from < 0 || to < 0 || from === to) return items;
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

export function useDashboardOrder() {
  const [order, setOrder] = useState<DashboardWidgetId[]>(readOrder);

  const reorder = useCallback((activeId: DashboardWidgetId, overId: DashboardWidgetId) => {
    setOrder((current) => {
      const next = moveById(current, activeId, overId);
      window.localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  }, []);

  return useMemo(() => ({ order, reorder }), [order, reorder]);
}
